import { PlaylistDownloadDto } from "collaboration-service";
import SafeHTML from "components/SafeHTML/SafeHTML";
import { Icon, ImgIcons, useThemePart } from "imaginarity-react-ui";
import { FloatedRight } from "layout/masterLayouts/MasterLayoutsSC";
import * as React from "react";
import { ApplicationState, useAppSelector } from "services/ApplicationState/ApplicationState";
import { getLink, shallowCompare } from "services/Helpers";
import { windowOpen } from "services/Helpers/LinkHelpers";
import { createNamedRoute } from "services/Helpers/RoutingHelper";
import { sanitizedNothingAllowedAttributes } from "services/Helpers/SanitizedHelper";
import { getTranslated } from "services/Helpers/TranslationHelpers";
import { useImgI18N } from "services/ImgI18N";
import { PlaylistDetailNodeItemProps, useHidePlaylistNodeItem } from "../PlaylistDetailNodeItem";
import { PlaylistDetailSC as T } from "../PlaylistDetailSC";
import { documentKinds } from "./usePlaylistButtonText";

const mapper = (s: ApplicationState) => ({
    contentLanguage: s.contentLanguage,
    params: s.params,
});

export const documentKindMap: { [mediaType: string]: documentKinds } = {
    "audio/mpeg": "audio",
    "image/tiff": "download",
    "video/mp4": "video",
    "video/quicktime": "video",
    "application/pdf": "pdf",
    "application/zip": "download",
    "application/x-zip-compressed": "download",
    "scorm-package": "wbt",
}

export const documentKindByType = (type?: string): documentKinds => {
    if (!type)
        return "document";
    var toRet = documentKindMap[type];
    if (toRet)
        return toRet;
    if (type.startsWith("image"))
        return "image";
    if (type.startsWith("application"))
        return "document";
    if (type === "url-file")
        return "url-file";
    if (type === "scorm-package")
        return "wbt";
    return "document";
}

export type PlaylistNodeItemDataType = {
    image: string,
    link?: string,
    icon: ImgIcons,
    title: string,
    type?: string,
};

const PlaylistPlaylistNodeItem = (p: PlaylistDetailNodeItemProps<PlaylistDownloadDto>) => {
    const { item, state, playlistContent, playlistStateId, isOptional, nodeReference, transition, warning, isMobile, nodeIsDone } = p;
    const { contentLanguage } = useAppSelector(mapper, shallowCompare);
    const { t, currentLanguage } = useImgI18N("playlist");
    const emptyImageUrl = useThemePart(t => t.emptyImageUrl);
    const language = contentLanguage ?? currentLanguage;
    const elementIsReference = item.id === nodeReference;

    const link = createNamedRoute("playlist_detail", { id: item.id, contextId: playlistStateId ?? "" }, true);
    const openLink = React.useMemo(() => () => {
        if (link)
            windowOpen(link, "_blank");
    }, [link]);
    const result = useHidePlaylistNodeItem(p);

    const { title, image } = React.useMemo(() => {

        return {
            title: getTranslated(item.headlines, language)?.text ?? "",
            image: getLink(item.media, "self")?.uri ?? emptyImageUrl,
        }

    }, [emptyImageUrl, item, language,]);

    if (result.hide) return null;

    const isDone = result.text === "100%";

    return (
        <T.PLItemContainer isMobile={isMobile} isPreview={p.preview ?? false} >
            <T.PLItemImage imageUrl={image} isMobile={isMobile} highlighted={elementIsReference} color={warning ? "@warning" : "@accentRed"} onClick={openLink} />

            <T.PLItemContainerContent isMobile={isMobile} onClick={openLink}>
                <T.PLItemContainerTitle isMobile={isMobile}>
                    {elementIsReference && <T.PLItemContainerTitleDot color={warning ? "@warning" : "@accentRed"} />}
                    <SafeHTML html={title} allowedTags={[]} allowedAttributes={{}} />
                </T.PLItemContainerTitle>
                {!isMobile && item.descriptions && (
                    <T.PLItemContainerDescription>
                        <SafeHTML
                            html={getTranslated(item.descriptions, language)?.text ?? ""}
                            allowedTags={["strong", "span", "b", "i", "u"]}
                            allowedAttributes={sanitizedNothingAllowedAttributes}
                        />
                    </T.PLItemContainerDescription>
                )}
                <T.PLItemContainerBottomCat isMobile={isMobile}>
                    <Icon name="playlist" marginTop={3} style={{ float: "left", marginLeft: -2, marginRight: 10 }} size="16px" />
                    {item.type}
                    {playlistContent?.duration && (
                        <>
                            <span style={{ padding: "0 10px" }}>&bull;</span>
                            {`${Math.floor(playlistContent.duration * 60 * 100) / 100} Min`}
                        </>
                    )}
                </T.PLItemContainerBottomCat>
            </T.PLItemContainerContent>

            {p.preview !== true && (
                <T.PLItemContainerRight isMobile={isMobile} onClick={openLink}>
                    {isOptional ? (
                        <T.ColoredDiv color={state.state === "Finished" ? "@accentGreen" : "@lightGrey"}>
                            <FloatedRight>{state.state === "Finished" ? t("done") : t("optional")}</FloatedRight>
                            <Icon name={state.state === "Finished" ? "check circle" : "empty"} style={{ float: "right", marginRight: 5 }} marginTop={4} color={state.state === "Finished" ? "@accentGreen" : "inherit"} />
                        </T.ColoredDiv>
                    ) : (
                        (transition === "ReferenceDone" || transition === "ReferenceDoneWithValue") ? (
                            elementIsReference ? (
                                <T.ColoredDiv color={(isDone || nodeIsDone) ? "@accentGreen" : warning ? "@warning" : result.color}>
                                    <FloatedRight>
                                        {(isDone || nodeIsDone) ? t("done") : warning ? t("warning") : t(result.text)}
                                    </FloatedRight>
                                    <Icon name={(isDone || nodeIsDone) ? "check circle" : warning ? "exclamation triangle" : result.icon as ImgIcons} style={{ float: "right", marginRight: 5 }} marginTop={4} color={(isDone || nodeIsDone) ? "@accentGreen" : warning ? "@warning" : result.color} />
                                </T.ColoredDiv>
                            ) : (
                                <T.ColoredDiv color={isDone ? "@accentGreen" : "@middleLightGrey"}>
                                    <FloatedRight>{isDone ? t("done") : t("optional")}</FloatedRight>
                                    <Icon name="empty" style={{ float: "right", marginRight: 5 }} marginTop={4} />
                                </T.ColoredDiv>
                            )
                        ) : (
                            <T.ColoredDiv color={isDone ? "@accentGreen" : warning ? "@warning" : result.color}>
                                <FloatedRight>
                                    {isDone ? t("done") : t(result.text)}
                                </FloatedRight>
                                <Icon
                                    name={result.icon as ImgIcons}
                                    style={{ float: "right", marginRight: 5 }}
                                    marginTop={4}
                                    color={isDone ? "@accentGreen" : warning ? "@warning" : result.color}
                                />
                            </T.ColoredDiv>
                        )
                    )}
                </T.PLItemContainerRight>
            )}


        </T.PLItemContainer>
    );
};

export default PlaylistPlaylistNodeItem;
